import { useState, useEffect, useRef } from "react";
import { Button, Space, Input, DatePicker } from "antd";
import { SearchOutlined, FilterOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { toastError } from "helpers/toasters";
import moment from "moment";
import { handleAllowNumbersOnly } from "helpers/helpfulFunctions";
import { GetAcceptedCashCollectionFromWalletForAdminForBuyAndSupply } from "Network";

// components
import GridTable from "modules/SubComponents/GridTable/GridTable";
import Header from "modules/SubComponents/Header";
import SubHeader from "modules/SubComponents/SubHeader";
import ViewBankDetails from "./ViewBankDetails";
import InvoiceModal from "./InvoiceModal";

const Withdrawls = () => {
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renderMe, setRenderMe] = useState(false);
  const [bankDetails, setBankDetails] = useState(null);
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [selectedRange, setSelectedRange] = useState([]);
  const [filteredDataSource, setFilteredDataSource] = useState([]);

  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const [withdrawInvoiceModalOpen, setWithdrawInvoiceModalOpen] =
    useState(false);
  const [invoiceRowData, setInvoiceRowData] = useState(null);

  const { RangePicker } = DatePicker;

  const exportConfig = {
    tab: "Withdrawals",
    baseProps: [
      { title: "Name", prop: "name" },
      { title: "Creation Date", prop: "creationDate" },
      { title: "Collection Type", prop: "gulfCashCollectionType" },
      { title: "Amount", prop: "amount" },
      { title: "Mobile", prop: "mobile" },
    ],
  };

  const handleOpenWithdrawInvoice = (row) => {
    setInvoiceRowData({
      ...row,
      fullName: row.name,
      deliveryDate: row.creationDate,
      supplyTotalPrice: row.amount,
    });
    setWithdrawInvoiceModalOpen(true);
  };

  const handleCloseWithdrawInvoice = () => {
    setWithdrawInvoiceModalOpen(false);
    setInvoiceRowData(null);
  };

  const handleDateChange = (dates) => {
    setSelectedRange(dates);
    if (dates) {
      const startDate = dates[0].format("DD MMM, YYYY");
      const endDate = dates[1].format("DD MMM, YYYY");
      const dataWithingRange = dataSource.filter((ele) => {
        const comparisonDate = moment(ele.creationDate, "DD MMM, YYYY");
        return (
          comparisonDate.isSameOrAfter(startDate) &&
          comparisonDate.isSameOrBefore(endDate)
        );
      });
      setFilteredDataSource(dataWithingRange);
    } else {
      setFilteredDataSource(dataSource);
    }
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onKeyDown={(e) => {
            const acceptsNumbersOnly = ["mobile"];
            if (acceptsNumbersOnly.includes(dataIndex)) {
              handleAllowNumbersOnly(e, true);
            }
          }}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() =>
              clearFilters && handleReset(clearFilters, dataIndex, confirm)
            }
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({
                closeDropdown: false,
              });
              setSearchText(selectedKeys[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          ></Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#1890ff" : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const getColumnDateProps = () => ({
    filterDropdown: () => (
      <div
        style={{
          padding: 8,
        }}
        className="range-picker-holder"
        onKeyDown={(e) => e.stopPropagation()}
      >
        <RangePicker
          value={selectedRange}
          allowClear
          popupClassName="main-date-picker"
          inputReadOnly={false}
          onChange={handleDateChange}
          onKeyDown={(e) => e.preventDefault()}
        />
      </div>
    ),
    filterIcon: (filtered) => (
      <FilterOutlined
        style={selectedRange?.length > 0 ? { color: "blue" } : {}}
      />
    ),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) => moment(text).format("DD/MM/YYYY"),
  });

  const columns = [
    {
      key: "name",
      title: "NAME",
      dataIndex: "name",
      ...getColumnSearchProps("name"),
    },
    {
      key: "creationDate",
      title: "CREATION DATE",
      dataIndex: "creationDate",
      ...getColumnDateProps("creationDate"),
    },
    {
      key: "gulfCashCollectionType",
      title: "COLLECTION TYPE",
      dataIndex: "gulfCashCollectionType",
      ...getColumnSearchProps("gulfCashCollectionType"),
    },
    {
      key: "amount",
      title: "AMOUNT",
      dataIndex: "amount",
      ...getColumnSearchProps("amount"),
    },
    {
      key: "mobile",
      title: "MOBILE",
      dataIndex: "mobile",
      ...getColumnSearchProps("mobile"),
    },
    {
      key: "id",
      title: "ACTIONS",
      render: (_, row) => {
        return (
          <div>
            <Space size={5} className="mb-2" wrap>
              <Button
                type="default"
                onClick={() => handleOpenWithdrawInvoice(row)}
              >
                Withdrawal Invoice
              </Button>
              {row.gulfCashCollectionType === "Bank Transfer" && (
                <Button
                  type="primary"
                  onClick={() => {
                    setBankDetails(row.bankDetails);
                    setShowBankDetails(true);
                  }}
                >
                  Bank Details
                </Button>
              )}
            </Space>
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    GetAcceptedCashCollectionFromWalletForAdminForBuyAndSupply(
      (res) => {
        setLoading(false);
        if (res.success) {
          setDataSource(res.data);
          setFilteredDataSource(res.data);
        } else {
          toastError(res.message);
        }
      },
      (res) => {
        setLoading(false);
        toastError(res?.data?.error);
      },
    );
  }, [renderMe]);

  return (
    <section className="users store-items">
      <div className="users__all-contents">
        <div className="users__content">
          <div className="users__usersData">
            <Header title="Withdrawls" />
            <div className="users__table store-items__data-table grid-table mrg-ye-50 ">
              <GridTable
                loading={loading}
                columns={columns}
                dataSource={filteredDataSource}
                rowId={"id"}
                allowExport={true}
                exportConfig={exportConfig}
                title={"Withdrawals"}
              />
            </div>

            {showBankDetails && (
              <ViewBankDetails
                rowData={bankDetails}
                open={showBankDetails}
                subHeader={
                  <SubHeader
                    bgColor="#589559"
                    title={`Bank Details`}
                    closeButton
                    onCancel={() => setShowBankDetails(false)}
                  />
                }
                onCancel={() => setShowBankDetails(false)}
              />
            )}

            <InvoiceModal
              open={withdrawInvoiceModalOpen}
              rowData={invoiceRowData}
              onClose={handleCloseWithdrawInvoice}
              mode="withdraw-invoice"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Withdrawls;
